import { useEffect, useRef, useState } from "react";
import { Clock, CalendarX, LayoutGrid, Phone, MapPin } from "lucide-react";
import { salonInfo } from "@/mocks/content";

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

const infoItems = [
  {
    icon: Clock,
    label: "営業時間",
    value: salonInfo.hours,
  },
  {
    icon: CalendarX,
    label: "定休日",
    value: salonInfo.closedDay,
  },
  {
    icon: LayoutGrid,
    label: "個室数",
    value: salonInfo.seats,
  },
  {
    icon: MapPin,
    label: "住所",
    value: salonInfo.address,
  },
];

export default function AccessSection() {
  const { ref, inView } = useInView(0.1);

  return (
    <section
      id="access"
      className="relative py-20 md:py-32"
      style={{ background: "#141414" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div
          ref={ref}
          className="mb-12 md:mb-16"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(30px)",
            transition: "all 0.8s cubic-bezier(0.23, 1, 0.32, 1)",
          }}
        >
          <div className="flex items-center gap-4 mb-4">
            <span className="w-12 h-px bg-[#D4A373]" />
            <span
              className="text-[#D4A373] text-xs tracking-[0.4em] uppercase"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              Access &amp; Info
            </span>
          </div>
          <h2
            className="text-white"
            style={{
              fontFamily: "'Noto Serif JP', serif",
              fontSize: "clamp(1.8rem, 4vw, 3rem)",
              letterSpacing: "0.05em",
            }}
          >
            アクセス・営業情報
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Map */}
          <div
            className="w-full lg:w-[55%]"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateX(0)" : "translateX(-30px)",
              transition: "all 0.9s 0.2s cubic-bezier(0.23, 1, 0.32, 1)",
              boxShadow: inView
                ? "0 20px 60px rgba(0,0,0,0.5)"
                : "0 0 0 rgba(0,0,0,0)",
            }}
          >
            <div className="relative overflow-hidden" style={{ paddingBottom: "65%" }}>
              <iframe
                src={salonInfo.mapEmbedUrl}
                className="absolute inset-0 w-full h-full border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="サロンの地図"
                style={{ filter: "grayscale(30%) contrast(1.1)" }}
              />
            </div>
          </div>

          {/* Info */}
          <div
            className="w-full lg:w-[45%] flex flex-col justify-center"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateX(0)" : "translateX(30px)",
              transition: "all 0.9s 0.3s cubic-bezier(0.23, 1, 0.32, 1)",
            }}
          >
            {/* Info Items */}
            <div className="space-y-6 mb-8">
              {infoItems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className="flex items-start gap-4 pb-6 border-b border-white/8"
                    style={{
                      opacity: inView ? 1 : 0,
                      transform: inView ? "translateY(0)" : "translateY(20px)",
                      transition: `all 0.6s ${0.4 + i * 0.1}s cubic-bezier(0.23, 1, 0.32, 1)`,
                    }}
                  >
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={16} className="text-[#D4A373]" />
                    </div>
                    <div>
                      <div
                        className="text-white/40 text-xs tracking-widest mb-1"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                      >
                        {item.label}
                      </div>
                      <div
                        className="text-white text-sm leading-relaxed"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                      >
                        {item.value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Phone */}
            <div
              className="mb-8 p-5 border border-[#D4A373]/20"
              style={{
                background: "rgba(212,163,115,0.05)",
                opacity: inView ? 1 : 0,
                transition: "opacity 0.8s 0.8s",
              }}
            >
              <div
                className="text-white/40 text-xs tracking-widest mb-2"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                お電話でのご予約
              </div>
              <a
                href={`tel:${salonInfo.phone}`}
                className="flex items-center gap-3 text-white hover:text-[#D4A373] transition-colors duration-300 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <Phone size={16} className="text-[#D4A373]" />
                </div>
                <span
                  className="text-xl font-medium tracking-wider"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {salonInfo.phone}
                </span>
              </a>
            </div>

            {/* SNS Links */}
            <div
              style={{
                opacity: inView ? 1 : 0,
                transition: "opacity 0.8s 0.9s",
              }}
            >
              <div
                className="text-white/40 text-xs tracking-widest mb-4"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                SNS
              </div>
              <div className="flex items-center gap-4">
                {/* Instagram */}
                <a
                  href={salonInfo.instagram}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group w-12 h-12 flex items-center justify-center border border-white/15 hover:border-[#D4A373]/60 hover:bg-[#D4A373]/10 transition-all duration-300 cursor-pointer"
                  aria-label="Instagram"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-white/60 group-hover:text-[#D4A373] transition-colors duration-300"
                  >
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </a>

                {/* LINE */}
                <a
                  href={salonInfo.line}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group w-12 h-12 flex items-center justify-center border border-white/15 hover:border-[#D4A373]/60 hover:bg-[#D4A373]/10 transition-all duration-300 cursor-pointer"
                  aria-label="LINE"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="text-white/60 group-hover:text-[#D4A373] transition-colors duration-300"
                  >
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
